import { createSwiper } from '@nulo/swiper';
import { safeFocus, whenNearViewport } from '@nulo/a11y';

/**
 * @extends {HTMLElement}
 */
class DiscoverNulo extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #mobileSwiper = null;

  /** @type {((event: Event) => void) | null} */
  #onClick = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeyDown = null;

  connectedCallback() {
    this.#bindDesktopHotspots();
    this.#scheduleMobileSlider();
  }

  disconnectedCallback() {
    if (this.#onClick) this.removeEventListener('click', this.#onClick);
    if (this.#onKeyDown) this.removeEventListener('keydown', this.#onKeyDown);

    this.#mobileSwiper?.destroy?.(true, true);
    this.#mobileSwiper = null;
  }

  #bindDesktopHotspots() {
    if (!this.querySelector('[data-discover-scene]')) return;

    this.#onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const pointer = target.closest('[data-discover-pointer]');
      if (pointer instanceof HTMLElement && this.contains(pointer)) {
        this.#openCard(pointer);
        return;
      }

      const closeBtn = target.closest('[data-discover-close]');
      if (closeBtn instanceof HTMLElement && this.contains(closeBtn)) {
        this.#closeAllCards();
        const activePointer = this.querySelector('[data-discover-pointer][aria-expanded="true"]');
        if (activePointer instanceof HTMLElement) safeFocus(activePointer);
        return;
      }

      const backdrop = target.closest('[data-discover-backdrop]');
      const cardPanel = target.closest('[data-discover-card-panel]');

      if (
        (backdrop instanceof HTMLElement && this.contains(backdrop)) ||
        (cardPanel instanceof HTMLElement && target === cardPanel)
      ) {
        this.#closeAllCards();
        const active = this.querySelector('[data-discover-pointer][aria-expanded="true"]');
        if (active instanceof HTMLElement) safeFocus(active);
      }
    };

    this.#onKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      const activePointer = this.querySelector('[data-discover-pointer][aria-expanded="true"]');
      if (!(activePointer instanceof HTMLElement)) return;

      this.#closeAllCards();
      safeFocus(activePointer);
    };

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  #closeAllCards() {
    const overlay = this.querySelector('[data-discover-overlay]');
    const imageWrap = this.querySelector('[data-discover-image-wrap]');

    if (overlay instanceof HTMLElement) overlay.hidden = true;
    imageWrap?.classList.remove('discover-nulo__image-wrap--active');

    this.querySelectorAll('[data-discover-card]').forEach((card) => {
      if (card instanceof HTMLElement) card.hidden = true;
    });

    this.querySelectorAll('[data-discover-pointer]').forEach((pointer) => {
      pointer.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * @param {HTMLElement} pointer
   */
  #openCard(pointer) {
    const cardId = pointer.getAttribute('data-card-id');
    const card = cardId ? this.querySelector(`#${cardId}`) : null;
    const overlay = this.querySelector('[data-discover-overlay]');
    const imageWrap = this.querySelector('[data-discover-image-wrap]');

    if (!(card instanceof HTMLElement) || !(overlay instanceof HTMLElement)) return;

    const isOpen = pointer.getAttribute('aria-expanded') === 'true';
    this.#closeAllCards();

    if (isOpen) return;

    overlay.hidden = false;
    imageWrap?.classList.add('discover-nulo__image-wrap--active');
    card.hidden = false;
    pointer.setAttribute('aria-expanded', 'true');

    const closeBtn = card.querySelector('[data-discover-close]');
    if (closeBtn instanceof HTMLElement) safeFocus(closeBtn);
  }

  async #scheduleMobileSlider() {
    const carousel = this.querySelector('[data-discover-mobile]');
    if (!(carousel instanceof HTMLElement)) return;

    await whenNearViewport(carousel);
    if (!this.isConnected) return;

    this.#initMobileSlider();
  }

  #initMobileSlider() {
    if (this.#mobileSwiper) return;

    const carousel = this.querySelector('[data-discover-mobile]');
    const swiperEl = carousel?.querySelector('[data-swiper]');

    if (!(carousel instanceof HTMLElement) || !(swiperEl instanceof HTMLElement)) return;

    this.#mobileSwiper = createSwiper({
      region: carousel,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config: {
        slidesPerView: 1.4,
        spaceBetween: 20,
        centeredSlides: false,
        navigation: {
          nextEl: carousel.querySelector('.discover-nulo__nav--next'),
          prevEl: carousel.querySelector('.discover-nulo__nav--prev'),
        },
        breakpoints: {
          575: {
            slidesPerView: 1.5,
            spaceBetween: 20,
          },
        },
      },
    });
  }
}

if (!customElements.get('discover-nulo')) {
  customElements.define('discover-nulo', DiscoverNulo);
}
